import React, { useState, useEffect, useMemo } from 'react';
import userService from '../services/userService';

function SiteAdminDashboard() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delayed loading spinner (150ms)
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowLoading(true), 150);
    } else {
      setShowLoading(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await userService.getSiteAdminOverview();
        setTeams(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const copyEmails = async (emails) => {
    try {
      await navigator.clipboard.writeText(emails);
      setCopiedEmail(emails);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch {
      // fallback ignored
    }
  };

  const toggleTeam = (teamId) => {
    setSelectedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTeams.size === teams.length) {
      setSelectedTeams(new Set());
    } else {
      setSelectedTeams(new Set(teams.map(t => t.team_id)));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await userService.deleteSiteAdminTeams([...selectedTeams]);
      setTeams(prev => prev.filter(t => !selectedTeams.has(t.team_id)));
      setSelectedTeams(new Set());
      setShowConfirm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const stats = useMemo(() => {
    if (!teams.length) return null;
    return {
      totalTeams: teams.length,
      withProducts: teams.filter(t => t.product_count > 0).length,
      withCustomers: teams.filter(t => t.customer_count > 0).length,
      withApiKeys: teams.filter(t => t.api_key_count > 0).length,
    };
  }, [teams]);

  if (loading) {
    if (!showLoading) return <div className="h-6" />;
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-950/30 border border-red-800/50 rounded-lg p-6 text-center">
        <h2 className="text-lg font-semibold mb-2 text-slate-100">Error</h2>
        <p className="text-slate-400 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-6">Site Admin Overview</h1>

      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-100">{stats.totalTeams}</div>
            <div className="text-sm text-slate-400">Total Teams</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-100">{stats.withProducts}</div>
            <div className="text-sm text-slate-400">With Products</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-100">{stats.withCustomers}</div>
            <div className="text-sm text-slate-400">With Customers</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-100">{stats.withApiKeys}</div>
            <div className="text-sm text-slate-400">With API Keys</div>
          </div>
        </div>
      )}

      {selectedTeams.size > 0 && !showConfirm && (
        <div className="mb-4">
          <button
            onClick={() => setShowConfirm(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg"
          >
            Delete {selectedTeams.size} team{selectedTeams.size !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {showConfirm && (
        <div className="mb-4 bg-red-950/30 border border-red-800/50 rounded-lg p-4">
          <p className="text-slate-100 text-sm font-medium mb-2">
            Permanently delete {selectedTeams.size} team{selectedTeams.size !== 1 ? 's' : ''} and all associated data?
          </p>
          <ul className="text-slate-400 text-xs mb-3 space-y-1 max-h-32 overflow-y-auto">
            {teams.filter(t => selectedTeams.has(t.team_id)).map(t => (
              <li key={t.team_id}>{t.team_name} ({t.org_name})</li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center gap-2"
            >
              {deleting && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              Confirm Delete
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              disabled={deleting}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 text-sm rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-left">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={teams.length > 0 && selectedTeams.size === teams.length}
                    onChange={toggleAll}
                    className="rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                  />
                </th>
                <th className="px-4 py-3 text-slate-400 font-medium">Team</th>
                <th className="px-4 py-3 text-slate-400 font-medium">Org</th>
                <th className="px-4 py-3 text-slate-400 font-medium">Admin Emails</th>
                <th className="px-4 py-3 text-slate-400 font-medium">Signed Up</th>
                <th className="px-4 py-3 text-slate-400 font-medium text-center">Members</th>
                <th className="px-4 py-3 text-slate-400 font-medium text-center">Products</th>
                <th className="px-4 py-3 text-slate-400 font-medium text-center">Customers</th>
                <th className="px-4 py-3 text-slate-400 font-medium text-center">API Keys</th>
                <th className="px-4 py-3 text-slate-400 font-medium text-center">Licenses</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.team_id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedTeams.has(team.team_id)}
                      onChange={() => toggleTeam(team.team_id)}
                      className="rounded border-slate-600 bg-slate-900 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-100 font-medium">{team.team_name}</td>
                  <td className="px-4 py-3 text-slate-300">{team.org_name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300 text-xs truncate max-w-[200px]">
                        {team.admin_emails || '—'}
                      </span>
                      {team.admin_emails && (
                        <button
                          onClick={() => copyEmails(team.admin_emails)}
                          className="text-xs text-indigo-400 hover:text-indigo-300 shrink-0"
                          title="Copy emails"
                        >
                          {copiedEmail === team.admin_emails ? '✓' : 'Copy'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {team.created_at ? new Date(team.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-300">{team.member_count}</td>
                  <td className="px-4 py-3 text-center text-slate-300">{team.product_count}</td>
                  <td className="px-4 py-3 text-center text-slate-300">{team.customer_count}</td>
                  <td className="px-4 py-3 text-center text-slate-300">{team.api_key_count}</td>
                  <td className="px-4 py-3 text-center text-slate-300">{team.license_grant_count}</td>
                </tr>
              ))}
              {teams.length === 0 && (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-slate-500">
                    No teams found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SiteAdminDashboard;
